import { simulate } from '../mock-utils'
import { MOCK_STAY_HOTEL } from '../reserva/mock'
import { deriveKeyStatus, entryGuideItem } from './key'
import { directionsFromStay } from './real'
import type { AccessCode, AccesosService, AccessInstructions, Directions, KeyStatus } from './types'

/**
 * Datos de ejemplo del bundle (inventario D4): Apartamentos Centro Histórico con cerradura conectada y
 * código de teclado (PantallaAccesos.dc.html, AccesosDetalle.dc.html `code` y `directions`) y Hotel
 * Mirador del Puerto sin cerradura (AccesosDetalle.dc.html `hotel`).
 * Códigos:
 *  - `llave-activa` / `llave-bloqueada` / `llave-caducada` → fuerza el estado de la llave
 *  - `interfono` → el portal se abre con la guía de dos pasos del interfono
 *  - `sin-codigo` → la puerta no tiene teclado
 *  - `puerta-falla` → la puerta no responde · `hotel` o `sin-cerradura` → sin cerradura conectada
 *  - `error-accesos`, `offline-accesos`, `lento-accesos` → services/mock-utils.ts
 */
const code = (validFrom: string, validUntil: string): AccessCode => ({
  value: '4821',
  validFrom,
  validUntil,
  howTo: ['Toca el teclado para encenderlo', 'Marca 4 8 2 1', 'Pulsa ✓ y gira el pomo'],
  troubleshooting: 'Si el teclado parpadea en rojo, espera 10 segundos y vuelve a empezar. Si no funciona dos veces, llama al teléfono de abajo.',
})

/** Textos del mock de la app actual (findreserve_guestapp.mock.ts: paso1, interfono, paso2). */
const INTERCOM = { step1: 'Pulsa primero el telefonillo', label: '4.º', step2: 'Abre el portal pulsando aquí debajo' }

export const MOCK_INSTRUCTIONS_HOTEL: AccessInstructions = {
  reception: {
    open24h: true,
    hours: null,
    afterHoursTitle: 'Si llegáis después de medianoche',
    afterHoursText: 'La recepción sigue abierta. Si la puerta de la calle está cerrada, llamad al timbre o al teléfono de abajo.',
  },
  steps: [
    { order: 1, title: 'Entra por el paseo del Puerto', text: 'Puerta de cristal con toldo azul. Hay rampa a la izquierda de la escalera.', photoUrl: null, photoAlt: 'Foto de la entrada del hotel' },
    { order: 2, title: 'Di tu nombre en recepción', text: 'Ya estáis registrados: no hace falta rellenar nada. Os darán dos tarjetas de la habitación.', photoUrl: null, photoAlt: 'Foto del mostrador de recepción' },
    { order: 3, title: 'Habitación 304, tercera planta', text: 'Ascensor a la derecha del mostrador. Al salir, girad a la izquierda; es la cuarta puerta.', photoUrl: null, photoAlt: 'Foto del pasillo de la tercera planta' },
    { order: 4, title: 'Acercad la tarjeta al lector', text: 'Luz verde y un clic: empujad la puerta. Si sale luz roja, esperad dos segundos y repetid.', photoUrl: null, photoAlt: 'Foto del lector de tarjeta de la puerta' },
  ],
  guideItem: null,
  emergencyPhone: MOCK_STAY_HOTEL.contact.emergencyPhone,
}

const DIRECTIONS_DEMO: Omit<Directions, 'street' | 'locality' | 'lat' | 'lng' | 'emergencyPhone'> = {
  addressNote: 'portal marrón junto a la farmacia',
  transport: [
    { mode: 'plane_metro', title: 'Desde el aeropuerto (avión + metro)', text: 'Metro L8 hasta Nuevos Ministerios, cambio a L10 hasta Ópera. 45 min, 5 € con el suplemento de aeropuerto. Desde Ópera, 4 min a pie.' },
  ],
  parking: { text: 'Zona peatonal: no se puede aparcar en la puerta. Parking Plaza Mayor a 150 m, 28 €/día. Podemos reservarlo por 18 €/noche desde Extras.', extraId: 'parking' },
  afterHoursText: 'La llave digital funciona a cualquier hora a partir de las 16:00. No hay recepción: si algo falla, el teléfono de abajo atiende 24 h.',
}

/** El bundle no trae "Cómo llegar" del hotel: sin transporte ni aparcamiento, para ver el estado solo con dirección. */
const DIRECTIONS_HOTEL: typeof DIRECTIONS_DEMO = { addressNote: null, transport: [], parking: null, afterHoursText: null }

const withoutLocks = (guestCode: string) => guestCode.includes('sin-cerradura')

function forcedKey(key: KeyStatus, guestCode: string): KeyStatus {
  const state = guestCode.includes('llave-activa') ? 'active' : guestCode.includes('llave-bloqueada') ? 'locked' : guestCode.includes('llave-caducada') ? 'expired' : key.state
  return { ...key, state: key.state === 'none' ? 'none' : state }
}

export const accesosMock: AccesosService = {
  async getKey(stay, signal) {
    await simulate(stay.guestCode, 'accesos', signal)
    if (withoutLocks(stay.guestCode)) return deriveKeyStatus({ ...stay, dispositives: [] })
    const key = forcedKey(deriveKeyStatus(stay), stay.guestCode)
    const accesses = key.accesses.map((a) => ({
      ...a,
      intercom: a.kind === 'portal' && stay.guestCode.includes('interfono') ? INTERCOM : a.intercom,
      code: a.kind === 'door' && !stay.guestCode.includes('sin-codigo') ? code(stay.dates.checkIn, stay.dates.checkOut) : a.code,
    }))
    return { ...key, accesses }
  },
  /** Abre en 1,5 s; con `puerta-falla` en el código, la puerta no responde. */
  async open(stay, accessId) {
    await new Promise((r) => setTimeout(r, 1500))
    if (stay.guestCode.includes('puerta-falla')) return { status: 'error', message: 'mock' }
    if (!stay.dispositives.some((d) => d.id === accessId)) return { status: 'error', message: 'no_command' }
    return { status: 'opened' }
  },
  async getInstructions(stay, signal) {
    await simulate(stay.guestCode, 'accesos', signal)
    if (stay.guestCode.includes('hotel') || withoutLocks(stay.guestCode)) return { ...MOCK_INSTRUCTIONS_HOTEL, emergencyPhone: stay.contact.emergencyPhone }
    return { reception: null, steps: [], guideItem: entryGuideItem(stay), emergencyPhone: stay.contact.emergencyPhone }
  },
  async getDirections(stay, signal) {
    await simulate(stay.guestCode, 'accesos', signal)
    return { ...directionsFromStay(stay), ...(stay.guestCode.includes('hotel') ? DIRECTIONS_HOTEL : DIRECTIONS_DEMO) }
  },
}
